import React, { useState, useEffect, useCallback } from 'react';
import { peopleApi, analysisApi, relationshipsApi } from '../api';
import DiaryList from './DiaryList';
import PersonalityDisplay from './PersonalityDisplay';
import PersonForm from './PersonForm';
import type { Person } from '../types/person';

interface PersonDetailProps {
  personId: number;
  onBack: () => void;
  onPersonDeleted: () => void;
}

const PersonDetail: React.FC<PersonDetailProps> = ({ personId, onBack, onPersonDeleted }) => {
  const [person, setPerson] = useState<Person | null>(null);
  const [activeTab, setActiveTab] = useState<'timeline' | 'relations' | 'analysis'>('timeline');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [diaryStats, setDiaryStats] = useState<any>(null);
  const [heatmap, setHeatmap] = useState<any>(null);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [otherPeople, setOtherPeople] = useState<any[]>([]);
  const [showRelForm, setShowRelForm] = useState(false);
  const [relForm, setRelForm] = useState({ other_person_id: '', relationship_type: '', strength: 0.5, description: '' });
  const [strengthLoading, setStrengthLoading] = useState(false);

  const loadPerson = useCallback(async () => {
    try {
      setLoading(true);
      const response = await peopleApi.getById(personId);
      const data = response.data;
      setPerson(data);
      setDiaryStats(data.diary_stats || null);
      setHeatmap(data.heatmap || null);
      setRelationships(data.relationships || []);
      setOtherPeople(data.other_people || []);
      setError(null);
    } catch (err: any) {
      setError('加载人员信息失败: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => { loadPerson(); }, [loadPerson]);

  const handleDelete = async () => {
    if (window.confirm('确定要删除这个人员吗？')) {
      try {
        await peopleApi.delete(personId);
        onPersonDeleted();
      } catch (error: any) {
        alert('删除失败: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await analysisApi.analyze(personId);
      setActiveTab('analysis');
      loadPerson();
    } catch (error: any) {
      alert('分析失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  const handleEditSubmit = () => { setShowEditForm(false); loadPerson(); };

  const handleAutoStrength = async () => {
    setStrengthLoading(true);
    try {
      const res = await peopleApi.autoStrength(personId);
      alert(res.data.message);
      loadPerson();
    } catch (err: any) {
      alert('计算失败');
    } finally {
      setStrengthLoading(false);
    }
  };

  const handleAddRelation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!relForm.other_person_id || !relForm.relationship_type) return;
    try {
      await relationshipsApi.create({
        person1_id: personId,
        person2_id: parseInt(relForm.other_person_id),
        relationship_type: relForm.relationship_type,
        strength: relForm.strength,
        description: relForm.description || undefined,
      });
      setShowRelForm(false);
      setRelForm({ other_person_id: '', relationship_type: '', strength: 0.5, description: '' });
      loadPerson();
    } catch (err: any) {
      alert('添加失败: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteRelation = async (rel: any) => {
    if (!window.confirm('删除此关系？')) return;
    try {
      await relationshipsApi.delete(personId, rel.other_person.id);
    } catch {
      try { await relationshipsApi.delete(rel.other_person.id, personId); } catch {}
    }
    loadPerson();
  };

  const accentColor = person?.color || '#6C5CE7';

  if (loading) {
    return (
      <div className="pd-loading">
        <div className="pd-loading-spinner" />
        <p>加载中...</p>
        <style>{`.pd-loading{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;gap:16px;}.pd-loading-spinner{width:36px;height:36px;border:3px solid var(--border);border-top-color:var(--primary);border-radius:50%;animation:pdSpin .8s linear infinite;}@keyframes pdSpin{to{transform:rotate(360deg)}}.pd-loading p{color:var(--text-tertiary);font-size:14px;}`}</style>
      </div>
    );
  }

  if (error || !person) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', padding:'60px 24px', textAlign:'center' }}>
        <div style={{ fontSize:48, marginBottom:16 }}>😕</div>
        <h3 style={{ fontSize:16, color:'var(--text-secondary)', margin:'0 0 24px', fontWeight:500 }}>{error || '人员不存在'}</h3>
        <button onClick={onBack} style={{ padding:'10px 20px', border:'1.5px solid var(--border)', borderRadius:12, background:'var(--surface)', fontSize:14, fontWeight:600, color:'var(--text-secondary)', cursor:'pointer', fontFamily:'inherit' }}>返回列表</button>
      </div>
    );
  }

  return (
    <div className="pd-page">
      {/* Hero Card */}
      <div className="pd-hero" style={{ borderLeft: `4px solid ${accentColor}` }}>
        <div className="pd-hero-nav">
          <button className="pd-nav-btn" onClick={onBack}>←</button>
          <div style={{ position:'relative' }}>
            <button className="pd-nav-btn" onClick={() => setShowActions(!showActions)}>⋯</button>
            {showActions && (
              <div className="pd-dropdown" onClick={() => setShowActions(false)}>
                <button className="pd-dropdown-item" onClick={() => { setShowEditForm(true); setShowActions(false); }}>✏️ 编辑</button>
                <button className="pd-dropdown-item danger" onClick={handleDelete}>🗑️ 删除</button>
              </div>
            )}
          </div>
        </div>

        <div className="pd-hero-top">
          <div className="pd-avatar" style={{ background: accentColor }}>{person.avatar || person.name?.charAt(0)}</div>
          <div className="pd-hero-info">
            <div className="pd-hero-name">{person.name}</div>
            {person.relationship_type && <div className="pd-hero-rel">{person.relationship_type}</div>}
          </div>
        </div>

        {person.notes && <div className="pd-hero-notes">{person.notes}</div>}

        {person.tags && person.tags.length > 0 && (
          <div className="pd-tags">
            {person.tags.map((tag, i) => <span key={i} className="pd-tag">{tag}</span>)}
          </div>
        )}

        {/* 互动频率统计 */}
        {diaryStats && (
          <div className="pd-stats">
            <div className="pd-stat"><div className="pd-stat-num">{diaryStats.count_7d}</div><div className="pd-stat-label">近7天</div></div>
            <div className="pd-stat"><div className="pd-stat-num">{diaryStats.count_30d}</div><div className="pd-stat-label">近30天</div></div>
            <div className="pd-stat"><div className="pd-stat-num">{diaryStats.count_90d}</div><div className="pd-stat-label">近90天</div></div>
          </div>
        )}
      </div>

      {/* 3-Tab */}
      <div className="pd-tabs">
        <button className={`pd-tab ${activeTab === 'timeline' ? 'active' : ''}`} onClick={() => setActiveTab('timeline')}>📅 互动</button>
        <button className={`pd-tab ${activeTab === 'relations' ? 'active' : ''}`} onClick={() => setActiveTab('relations')}>🔗 关系</button>
        <button className={`pd-tab ${activeTab === 'analysis' ? 'active' : ''}`} onClick={() => setActiveTab('analysis')}>🧬 分析</button>
      </div>

      {/* ===== Tab 1: 互动记录 ===== */}
      {activeTab === 'timeline' && (
        <div className="pd-panel">
          {/* 热力图 */}
          {heatmap && heatmap.cells && heatmap.cells.length > 0 && (
            <div className="pd-hm-card">
              <div className="pd-hm-header">
                <span className="pd-hm-title">90天互动热力图</span>
                <span className="pd-hm-stats">活跃 {heatmap.total_active}/{heatmap.total_days} 天</span>
              </div>
              <div className="pd-hm-wrap">
                <div className="pd-hm-labels">
                  <span>一</span><span>二</span><span>三</span><span>四</span><span>五</span><span>六</span><span>日</span>
                </div>
                <div className="pd-hm-grid" style={{ gridTemplateColumns: `repeat(${heatmap.num_weeks},1fr)` }}>
                  {heatmap.cells.map((cell: any, i: number) => (
                    <div key={i} className="pd-hm-cell" style={{ background: cell.color }} title={cell.title} />
                  ))}
                </div>
              </div>
              <div className="pd-hm-legend">
                <span>少</span>
                {heatmap.legend?.map((c: string, i: number) => (
                  <span key={i} className="pd-hm-legend-box" style={{ background: c }} />
                ))}
                <span>多</span>
              </div>
            </div>
          )}
          <DiaryList personId={personId} />
        </div>
      )}

      {/* ===== Tab 2: 人际关系 ===== */}
      {activeTab === 'relations' && (
        <div className="pd-panel">
          <div className="pd-card">
            <div className="pd-card-header">
              <span className="pd-card-title">人际关系 ({relationships.length})</span>
              <div style={{ display:'flex', gap:4 }}>
                <button className="pd-btn-sm" onClick={handleAutoStrength} disabled={strengthLoading}>
                  {strengthLoading ? '计算中...' : '⚡ 自动计算'}
                </button>
                <button className="pd-btn-sm" onClick={() => setShowRelForm(!showRelForm)}>➕ 添加</button>
              </div>
            </div>

            {showRelForm && (
              <form className="pd-rel-form" onSubmit={handleAddRelation}>
                <select value={relForm.other_person_id} onChange={e => setRelForm({ ...relForm, other_person_id: e.target.value })} required>
                  <option value="">选择人物...</option>
                  {otherPeople.map(op => <option key={op.id} value={op.id}>{op.name}{op.relationship_type ? ` (${op.relationship_type})` : ''}</option>)}
                </select>
                <input type="text" placeholder="关系类型" value={relForm.relationship_type} onChange={e => setRelForm({ ...relForm, relationship_type: e.target.value })} required />
                <div className="pd-rel-presets">
                  {['朋友', '同事', '家人', '同学', '恋人'].map(t => (
                    <button key={t} type="button" className="pd-preset" onClick={() => setRelForm({ ...relForm, relationship_type: t })}>{t}</button>
                  ))}
                </div>
                <div className="pd-strength-row">
                  <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>强度</span>
                  <input type="range" min="0" max="1" step="0.1" value={relForm.strength} onChange={e => setRelForm({ ...relForm, strength: parseFloat(e.target.value) })} />
                  <span style={{ fontSize:12, fontWeight:700, color:'#34d399', minWidth:28 }}>{relForm.strength}</span>
                </div>
                <textarea placeholder="描述（可选）" value={relForm.description} onChange={e => setRelForm({ ...relForm, description: e.target.value })} rows={2} />
                <button type="submit" className="pd-btn-primary">添加关系</button>
              </form>
            )}

            {relationships.length === 0 ? (
              <div className="pd-empty-hint">暂无人际关系，点击上方按钮添加</div>
            ) : (
              relationships.map((rel: any) => (
                <div key={rel.id} className="pd-rel-card">
                  <div className="pd-rel-avatar" style={{ background: rel.other_person?.color || '#667eea' }}>
                    {rel.other_person?.name?.charAt(0) || '?'}
                  </div>
                  <div className="pd-rel-info">
                    <div className="pd-rel-name">{rel.other_person?.name}</div>
                    <div className="pd-rel-type">{rel.relationship_type}</div>
                  </div>
                  <div className="pd-strength-bar"><div className="pd-strength-fill" style={{ width: `${(rel.strength || 0) * 100}%` }} /></div>
                  <span className="pd-strength-text">{rel.strength}</span>
                  <button className="pd-rel-del" onClick={() => handleDeleteRelation(rel)}>✕</button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ===== Tab 3: 性格分析 ===== */}
      {activeTab === 'analysis' && (
        <div className="pd-panel">
          <PersonalityDisplay personId={personId} personName={person.name} />
          <button className="pd-btn-primary" style={{ width:'100%', marginTop:12 }} onClick={handleAnalyze} disabled={analyzing}>
            {analyzing ? '分析中...' : ((person as any).personality_records?.length ? '🔍 重新分析' : '🔍 开始 AI 分析')}
          </button>
        </div>
      )}

      {showEditForm && <PersonForm person={person} onClose={() => setShowEditForm(false)} onSubmit={handleEditSubmit} />}

      <style>{`
        .pd-page { animation: pdFadeIn 0.3s ease; }
        @keyframes pdFadeIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }

        /* Hero */
        .pd-hero { background:var(--surface); border:1px solid var(--border-light,rgba(255,255,255,0.06)); border-radius:20px; padding:16px; margin-bottom:16px; }
        .pd-hero-nav { display:flex; justify-content:space-between; margin-bottom:12px; }
        .pd-nav-btn { width:36px; height:36px; border:none; border-radius:10px; background:rgba(255,255,255,0.06); color:var(--text-secondary); font-size:18px; cursor:pointer; display:flex; align-items:center; justify-content:center; font-family:inherit; }
        .pd-nav-btn:active { transform:scale(0.9); }
        .pd-dropdown { position:absolute; right:0; top:100%; margin-top:4px; background:var(--surface); border:1px solid var(--border); border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,0.3); z-index:50; min-width:120px; padding:4px; }
        .pd-dropdown-item { display:block; width:100%; padding:10px 14px; border:none; background:none; font-size:13px; font-weight:500; color:var(--text-secondary); cursor:pointer; border-radius:6px; text-align:left; font-family:inherit; }
        .pd-dropdown-item:hover { background:var(--surface-hover); }
        .pd-dropdown-item.danger { color:var(--danger); }

        .pd-hero-top { display:flex; align-items:center; gap:14px; margin-bottom:10px; }
        .pd-avatar { width:56px; height:56px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:24px; font-weight:800; color:#fff; flex-shrink:0; }
        .pd-hero-info { flex:1; }
        .pd-hero-name { font-size:18px; font-weight:800; color:var(--text-primary); }
        .pd-hero-rel { font-size:12px; color:var(--text-tertiary); margin-top:2px; }
        .pd-hero-notes { font-size:12px; color:var(--text-tertiary); margin-bottom:10px; }
        .pd-tags { display:flex; gap:6px; flex-wrap:wrap; margin-bottom:12px; }
        .pd-tag { padding:3px 10px; border-radius:999px; font-size:10px; font-weight:600; background:rgba(139,92,246,0.12); color:#a78bfa; }

        /* Stats */
        .pd-stats { display:flex; gap:8px; }
        .pd-stat { flex:1; text-align:center; padding:10px 8px; background:rgba(17,24,39,0.4); border-radius:10px; }
        .pd-stat-num { font-size:20px; font-weight:800; color:var(--text-primary); }
        .pd-stat-label { font-size:9px; color:var(--text-tertiary); margin-top:2px; }

        /* Tabs */
        .pd-tabs { display:flex; gap:0; margin-bottom:16px; }
        .pd-tab { flex:1; padding:10px 8px; text-align:center; font-size:12px; font-weight:700; background:var(--surface); color:var(--text-tertiary); cursor:pointer; border:1px solid var(--border-light,rgba(255,255,255,0.06)); font-family:inherit; transition:all 0.2s; }
        .pd-tab:first-child { border-radius:12px 0 0 12px; }
        .pd-tab:last-child { border-radius:0 12px 12px 0; }
        .pd-tab.active { background:rgba(139,92,246,0.15); color:#a78bfa; border-color:rgba(139,92,246,0.3); }

        .pd-panel { animation: pdFadeIn 0.2s ease; }

        /* Heatmap */
        .pd-hm-card { background:var(--surface); border:1px solid var(--border-light,rgba(255,255,255,0.06)); border-radius:14px; padding:14px; margin-bottom:12px; }
        .pd-hm-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px; }
        .pd-hm-title { font-size:12px; font-weight:700; color:var(--text-primary); }
        .pd-hm-stats { font-size:10px; color:var(--text-tertiary); }
        .pd-hm-wrap { display:flex; gap:4px; }
        .pd-hm-labels { display:grid; grid-template-rows:repeat(7,11px); gap:2px; font-size:8px; color:var(--text-tertiary); align-items:center; }
        .pd-hm-grid { flex:1; display:grid; grid-auto-flow:column; grid-template-rows:repeat(7,11px); gap:2px; }
        .pd-hm-cell { border-radius:2px; min-width:0; }
        .pd-hm-legend { display:flex; align-items:center; gap:4px; margin-top:6px; font-size:9px; color:var(--text-tertiary); }
        .pd-hm-legend-box { width:10px; height:10px; border-radius:2px; display:inline-block; }

        /* Card */
        .pd-card { background:var(--surface); border:1px solid var(--border-light,rgba(255,255,255,0.06)); border-radius:14px; padding:16px; margin-bottom:12px; }
        .pd-card-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; }
        .pd-card-title { font-size:13px; font-weight:700; color:var(--text-primary); }

        /* Buttons */
        .pd-btn-sm { padding:4px 10px; border-radius:8px; font-size:11px; font-weight:600; background:rgba(30,41,59,0.6); color:var(--text-tertiary); border:1px solid rgba(255,255,255,0.06); cursor:pointer; font-family:inherit; }
        .pd-btn-sm:hover { background:rgba(139,92,246,0.12); color:#a78bfa; }
        .pd-btn-sm:disabled { opacity:0.5; cursor:not-allowed; }
        .pd-btn-primary { padding:10px 20px; border-radius:12px; font-size:13px; font-weight:600; background:linear-gradient(135deg,var(--primary),#d946ef); color:#fff; border:none; cursor:pointer; font-family:inherit; }
        .pd-btn-primary:active { transform:scale(0.97); }
        .pd-btn-primary:disabled { opacity:0.6; cursor:not-allowed; }

        /* Relation form */
        .pd-rel-form { background:rgba(15,23,42,0.4); border-radius:10px; padding:14px; margin-bottom:10px; display:flex; flex-direction:column; gap:8px; }
        .pd-rel-form select, .pd-rel-form input[type="text"], .pd-rel-form textarea {
          width:100%; padding:8px 10px; border-radius:8px; background:rgba(15,23,42,0.6); border:1px solid rgba(255,255,255,0.1);
          color:var(--text-primary); font-size:13px; box-sizing:border-box; font-family:inherit;
        }
        .pd-rel-form textarea { resize:vertical; min-height:40px; }
        .pd-rel-presets { display:flex; gap:4px; flex-wrap:wrap; }
        .pd-preset { padding:3px 8px; border-radius:6px; font-size:10px; background:rgba(30,41,59,0.6); color:var(--text-tertiary); border:1px solid rgba(255,255,255,0.06); cursor:pointer; font-family:inherit; }
        .pd-preset:hover { background:rgba(139,92,246,0.12); color:#a78bfa; }
        .pd-strength-row { display:flex; align-items:center; gap:8px; }
        .pd-strength-row input[type="range"] { flex:1; accent-color:#34d399; }

        /* Relation card */
        .pd-rel-card { display:flex; align-items:center; gap:10px; padding:10px; background:rgba(15,23,42,0.4); border-radius:10px; margin-bottom:6px; }
        .pd-rel-avatar { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:800; color:#fff; flex-shrink:0; }
        .pd-rel-info { flex:1; min-width:0; }
        .pd-rel-name { font-size:13px; font-weight:600; color:var(--text-primary); }
        .pd-rel-type { font-size:10px; color:var(--text-tertiary); }
        .pd-strength-bar { width:50px; height:4px; background:rgba(30,41,59,0.8); border-radius:2px; overflow:hidden; flex-shrink:0; }
        .pd-strength-fill { height:100%; background:#34d399; border-radius:2px; }
        .pd-strength-text { font-size:10px; color:var(--text-tertiary); min-width:24px; text-align:right; flex-shrink:0; }
        .pd-rel-del { background:none; border:none; font-size:11px; color:rgba(239,68,68,0.5); cursor:pointer; padding:2px 4px; }
        .pd-rel-del:hover { color:#f87171; }

        .pd-empty-hint { font-size:12px; color:var(--text-tertiary); padding:16px 0; text-align:center; }
      `}</style>
    </div>
  );
};

export default PersonDetail;
